import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { PaletteMode } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { makeTheme } from './theme';

interface ColorModeCtx {
  mode: PaletteMode;
  toggle: () => void;
}

const Ctx = createContext<ColorModeCtx>({ mode: 'light', toggle: () => { /* noop */ } });

const STORAGE_KEY = 'waf-admin-color-mode';

function initialMode(): PaletteMode {
  if (typeof window === 'undefined') return 'light';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  // Default to light regardless of OS theme preference. Respect a saved
  // choice if the user has previously toggled — that's persisted to
  // localStorage by the toggle handler.
  return 'light';
}

export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<PaletteMode>(initialMode);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const theme = useMemo(() => makeTheme(mode), [mode]);
  const ctxValue = useMemo<ColorModeCtx>(
    () => ({ mode, toggle: () => setMode((m) => (m === 'light' ? 'dark' : 'light')) }),
    [mode]
  );

  return (
    <Ctx.Provider value={ctxValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </Ctx.Provider>
  );
}

export const useColorMode = () => useContext(Ctx);
